import { Card, CardContent } from "@/components/ui/card";
import {
  AlertCircle,
  Download,
  LoaderIcon,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { userCreditQueries } from "@/api/user-credits";
import { useMemo, useState } from "react";
import { Persona } from "@/api/personas";
import { CreditConsumptionChart } from "@/components/account/user-analytics/CreditConsumptionChart";
import { DateRange } from "@/lib/date";

export function OverviewTab({
  user,
  dateRange,
}: {
  user: Persona;
  dateRange: DateRange;
}) {
  const username = user.persona_id;
  const [showApiCalls, setShowApiCalls] = useState(false);
  const [transactionPage] = useState(1);

  const { data: creditSummary, isLoading: isSummaryLoading } = useQuery(
    userCreditQueries.summary(username),
  );
  const { data: creditHistory, isLoading: isHistoryLoading } = useQuery(
    userCreditQueries.history(
      username,
      dateRange.from.toISOString(),
      dateRange.to.toISOString(),
    ),
  );
  const { data: transactions } = useQuery(
    userCreditQueries.transactions(username, transactionPage, 10),
  );

  const handleExportData = () => {
    if (!creditHistory) return;

    const csvContent = [
      ["Date", "Credits Used", "API Calls", "Avg Credits/Call"],
      ...creditHistory.daily_usage.map((day) => {
        const calls = day.api_calls ?? 0;
        const creds = day.credits_used ?? 0;
        const avg = calls ? (creds / calls).toFixed(2) : "0.00";
        return [day.date, creds.toString(), calls.toString(), avg];
      }),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `credit-usage-${
      username
    }-${format(dateRange.from, "yyyy-MM-dd")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const metrics = useMemo(() => {
    if (!creditHistory?.daily_usage || creditHistory.daily_usage.length === 0) {
      return null;
    }

    // 1) Sort usage by date ascending
    const usage = [...creditHistory.daily_usage].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    // 2) Compute total credits and total calls
    const totalCredits = usage.reduce(
      (sum, day) => sum + (day.amount ?? day.credits_used ?? 0),
      0,
    );
    const totalCalls = usage.reduce(
      (sum, day) => sum + (day.api_calls ?? 0),
      0,
    );

    // 3) Derive a daily average usage (credits) from the first record date to the last record date
    const firstDate = new Date(usage[0].date);
    const lastDate = new Date(usage[usage.length - 1].date);
    const diffDays = Math.max(
      1,
      Math.ceil(
        (lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24),
      ),
    );
    const dailyAverageUsage = totalCredits / diffDays;

    // 4) Keep your usageTrend logic, but base it on the “amount” (or .credits_used):
    const midPoint = Math.floor(usage.length / 2);
    const firstHalf = usage.slice(0, midPoint);
    const secondHalf = usage.slice(midPoint);

    const firstHalfAvg = firstHalf.length
      ? firstHalf.reduce(
          (sum, day) => sum + (day.amount ?? day.credits_used ?? 0),
          0,
        ) / firstHalf.length
      : 0;

    const secondHalfAvg = secondHalf.length
      ? secondHalf.reduce(
          (sum, day) => sum + (day.amount ?? day.credits_used ?? 0),
          0,
        ) / secondHalf.length
      : 0;

    const usageTrend = firstHalfAvg
      ? ((secondHalfAvg - firstHalfAvg) / firstHalfAvg) * 100
      : 0;

    // 5) Return all the metrics
    return {
      totalCredits,
      totalCalls,
      avgCreditsPerCall: totalCalls ? totalCredits / totalCalls : 0,
      dailyAverageUsage,
      usageTrend,
    };
  }, [creditHistory]);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Available Credits */}
        <Card className="p-4">
          <CardContent className="p-0">
            <h4 className="text-sm font-medium text-muted-foreground">
              Available Credits
            </h4>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-bold font-mono">
                {isSummaryLoading ? (
                  <LoaderIcon className="h-6 w-6 animate-spin" />
                ) : (
                  (creditSummary?.available_credits ?? 0).toLocaleString()
                )}
              </p>
              <Badge
                variant={
                  (creditSummary?.available_credits ?? 0) < 1000
                    ? "destructive"
                    : "default"
                }
              >
                {(creditSummary?.available_credits ?? 0) < 1000
                  ? "Low Balance"
                  : "Healthy"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Credits Used */}
        <Card className="p-4">
          <CardContent className="p-0">
            <h4 className="text-sm font-medium text-muted-foreground">
              Credits Used (Period)
            </h4>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-bold font-mono">
                {isHistoryLoading ? (
                  <LoaderIcon className="h-6 w-6 animate-spin" />
                ) : (
                  (metrics?.totalCredits ?? 0).toLocaleString()
                )}
              </p>
              <div className="flex items-center gap-1">
                {(metrics?.usageTrend ?? 0) > 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                )}
                <span
                  className={
                    (metrics?.usageTrend ?? 0) > 0
                      ? "text-green-500"
                      : "text-red-500"
                  }
                >
                  {Math.abs(metrics?.usageTrend ?? 0).toFixed(1)}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Daily Average */}
        <Card className="p-4">
          <CardContent className="p-0">
            <h4 className="text-sm font-medium text-muted-foreground">
              Daily Average Usage
            </h4>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-bold font-mono">
                {isHistoryLoading ? (
                  <LoaderIcon className="h-6 w-6 animate-spin" />
                ) : (
                  (metrics?.dailyAverageUsage ?? 0).toLocaleString()
                )}
              </p>
              <Badge variant="outline">
                {creditHistory?.daily_usage?.length ?? 0} days
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* API Efficiency */}
        <Card className="p-4">
          <CardContent className="p-0">
            <h4 className="text-sm font-medium text-muted-foreground">
              Credits per API Call
            </h4>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-2xl font-bold font-mono">
                {isHistoryLoading ? (
                  <LoaderIcon className="h-6 w-6 animate-spin" />
                ) : (
                  (metrics?.avgCreditsPerCall ?? 0).toFixed(2)
                )}
              </p>
              <Badge>{(metrics?.totalCalls ?? 0).toLocaleString()} calls</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Usage Chart (ApexCharts) */}
      <Card className="p-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Usage Trends</h3>
            <p className="text-sm text-muted-foreground">
              Daily credit consumption and API calls
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowApiCalls(!showApiCalls)}
            >
              {showApiCalls ? "Hide API Calls" : "Show API Calls"}
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleExportData}
              disabled={isHistoryLoading}
            >
              <Download className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="h-[400px] relative">
          {isHistoryLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <LoaderIcon className="h-8 w-8 animate-spin" />
            </div>
          ) : creditHistory && creditHistory.daily_usage.length > 0 ? (
            <CreditConsumptionChart
              creditHistory={creditHistory}
              showApiCalls={showApiCalls}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
              <AlertCircle className="h-8 w-8 mb-2" />
              <p>No usage data available for the selected period</p>
            </div>
          )}
        </div>
      </Card>

      {/* User Info & Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* User Details */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">User Information</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium">{user.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Agent ID</span>
              <span className="font-medium">{user.persona_id}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-muted-foreground">Tenant</span>
              <Badge>{user.qstarlabs_tenant}</Badge>
            </div>
          </div>
        </Card>

        {/* Recent Activity */}
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="space-y-4">
            {transactions?.transactions?.slice(0, 5).map((tx) => (
              <div
                key={tx.transaction_id}
                className="flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{tx.transaction_type}</Badge>
                  <span className="text-sm text-muted-foreground">
                    {tx.timestamp &&
                      format(new Date(tx.timestamp), "MMM dd, HH:mm")}
                  </span>
                </div>
                <span
                  className={`font-mono ${
                    tx.transaction_type === "usage"
                      ? "text-red-500"
                      : "text-green-500"
                  }`}
                >
                  {tx.transaction_type === "usage" ? "-" : "+"}
                  {(tx.amount ?? 0).toLocaleString()}
                </span>
              </div>
            ))}
            {(!transactions?.transactions ||
              transactions.transactions.length === 0) && (
              <div className="text-center text-muted-foreground">
                No recent transactions
              </div>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
